import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, ugx, fmtDate } from "@/lib/api";
import { ORDER_STATUS_CLS } from "@/pages/host/Orders";
import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { CARD, TAG, drawCard, drawTag, save } from "@/pages/host/editor/PrintKit";
import { PORTRAIT, drawPortrait, loadImg } from "@/lib/portrait";

function DesignDownload({ dz, testId }) {
  const qr = useRef();
  const url = `${window.location.origin}${dz.link_path}`;
  const go = async () => {
    await document.fonts.ready;
    const c = document.createElement("canvas");
    const q = qr.current.querySelector("canvas");
    [c.width, c.height] = dz.kind === "card" ? CARD : dz.kind === "portrait" ? PORTRAIT : TAG;
    if (dz.kind === "card") drawCard(c, dz.property_name, q);
    else if (dz.kind === "portrait") drawPortrait(c, { name: dz.property_name, hostName: dz.host_name, qr: q, logo: await loadImg(dz.host_logo) });
    else drawTag(c, dz.label === "Main guest link" ? dz.property_name : dz.label, q);
    save(c, `${dz.property_name}-${dz.kind}.png`);
  };
  return (
    <div className="mt-2 rounded-lg bg-[#151312] p-2 text-xs" data-testid={testId}>
      <div className="hidden" ref={qr}><QRCodeCanvas value={url} size={400} marginSize={1} /></div>
      <p className="font-semibold text-[#E4A853]">{{ card: "Card design", portrait: "Portrait card design", keyholder: "Key holder design" }[dz.kind]}</p>
      <p className="text-[#A39E9A]">{dz.property_name} · {dz.label}</p>
      <div className="mt-1 flex gap-3">
        <button onClick={go} className="inline-flex items-center gap-1 font-semibold text-white hover:text-[#E4A853]" data-testid={`${testId}-download`}><DownloadSimple /> PNG</button>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#A39E9A] hover:text-white"><ArrowSquareOut /> {dz.link_path}</a>
      </div>
    </div>
  );
}

const STATUSES = ["Received", "In production", "Dispatched", "Delivered", "Cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  const update = async (id, status) => {
    try {
      const r = await api.patch(`/admin/orders/${id}`, { status });
      setOrders((o) => o.map((x) => (x.order_id === id ? r.data : x)));
      toast.success(`Order marked ${status}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">Card & key holder orders</h1>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-[#302B28] bg-[#1E1A18]">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead><tr className="border-b border-[#302B28] text-left text-xs uppercase tracking-wider text-[#A39E9A]">{["Order", "Host", "Items", "Delivery", "Total", "Status"].map((x) => <th key={x} className="px-5 py-3 font-semibold">{x}</th>)}</tr></thead>
          <tbody className="divide-y divide-[#302B28]">
            {orders && orders.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#A39E9A]">No orders yet.</td></tr>}
            {(orders || []).map((o) => (
              <tr key={o.order_id} data-testid={`admin-order-row-${o.order_id}`}>
                <td className="px-5 py-3"><p className="font-mono font-semibold">{o.number}</p><p className="text-xs text-[#A39E9A]">{fmtDate(o.created_at)}</p></td>
                <td className="px-5 py-3"><p className="font-semibold">{o.host_name}</p><p className="text-xs text-[#A39E9A]">{o.phone}</p></td>
                <td className="px-5 py-3">{o.cards} card{o.cards === 1 ? "" : "s"} · {o.keyholders} key holder{o.keyholders === 1 ? "" : "s"}{o.note && <p className="max-w-xs text-xs text-[#A39E9A]">{o.note}</p>}
                  {o.designs?.map((dz, i) => <DesignDownload key={i} dz={dz} testId={`admin-design-${o.order_id}-${i}`} />)}</td>
                <td className="px-5 py-3"><p>{o.delivery_label}</p>{o.address && <p className="max-w-xs text-xs text-[#A39E9A]">{o.address}</p>}</td>
                <td className="px-5 py-3"><p className="font-semibold">{ugx(o.total)}</p><p className="text-xs text-[#A39E9A]">{o.pay}</p></td>
                <td className="px-5 py-3">
                  <select value={o.status} onChange={(e) => update(o.order_id, e.target.value)} data-testid={`admin-order-status-${o.order_id}`} className={`rounded-full border-0 px-3 py-1 text-xs font-semibold ${ORDER_STATUS_CLS[o.status]}`}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
